/* Emerging Signals — extracted from the old standalone SignalsPage so it
   can render as a tab inside AlertsPage. Functionally identical. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Radar, RefreshCw, Check, X, Loader2, ExternalLink, AlertCircle, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const STATUS_FILTERS = [
  { key: 'pending_review', label: 'Pending Review', color: 'bg-amber-100 text-amber-800' },
  { key: 'accepted', label: 'Accepted', color: 'bg-emerald-100 text-emerald-700' },
  { key: 'dismissed', label: 'Dismissed', color: 'bg-slate-100 text-slate-500' },
  { key: 'no_signal', label: 'No Signal', color: 'bg-slate-100 text-slate-400' },
  { key: 'all', label: 'All', color: 'bg-slate-200 text-slate-700' },
];

const CONFIDENCE_COLORS = {
  'High': 'bg-emerald-100 text-emerald-700',
  'Medium': 'bg-amber-100 text-amber-700',
  'Low': 'bg-slate-100 text-slate-500',
};

export default function EmergingSignalsTab() {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [actionBusy, setActionBusy] = useState({});

  const fetchSignals = async (status = statusFilter) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/signals/detected?status=${status}`, { withCredentials: true });
      setSignals(res.data?.signals || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load signals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSignals(statusFilter); /* eslint-disable-next-line */ }, [statusFilter]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/signals/scan-gmail`, { days: 30, max_messages: 50 }, { withCredentials: true, timeout: 120000 });
      const { scanned_pharma_replies, new_candidates } = res.data;
      if (new_candidates > 0) {
        toast.success(`Scanned ${scanned_pharma_replies} pharma replies — ${new_candidates} new signal${new_candidates !== 1 ? 's' : ''} detected`);
      } else {
        toast(`Scanned ${scanned_pharma_replies} pharma replies — no new signals detected`, { icon: 'ℹ️' });
      }
      fetchSignals();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gmail scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleAccept = async (signalId, area) => {
    setActionBusy(prev => ({ ...prev, [signalId]: 'accepting' }));
    try {
      await axios.post(`${API}/signals/detected/${signalId}/accept`, {}, { withCredentials: true });
      toast.success(`Logged ${area} as an emerging interest`);
      setSignals(prev => prev.filter(s => s.id !== signalId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to accept');
    } finally {
      setActionBusy(prev => ({ ...prev, [signalId]: null }));
    }
  };

  const handleDismiss = async (signalId) => {
    setActionBusy(prev => ({ ...prev, [signalId]: 'dismissing' }));
    try {
      await axios.post(`${API}/signals/detected/${signalId}/dismiss`, {}, { withCredentials: true });
      toast.success('Dismissed');
      setSignals(prev => prev.filter(s => s.id !== signalId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setActionBusy(prev => ({ ...prev, [signalId]: null }));
    }
  };

  const pendingCount = statusFilter === 'pending_review' ? signals.length : null;

  return (
    <div data-testid="emerging-signals-tab">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="max-w-2xl">
          <p className="text-sm text-slate-500">
            Auto-detected off-list therapeutic area signals from pharma contact replies. Claude reads the reply, extracts any area the company is <i>reviewing</i>, <i>exploring</i>, or <i>scouting</i>, and surfaces it here for one-click acceptance.
          </p>
          {pendingCount !== null && pendingCount > 0 && (
            <Badge className="bg-amber-100 text-amber-800 text-xs mt-2">{pendingCount} pending</Badge>
          )}
        </div>
        <Button
          onClick={handleScan}
          disabled={scanning}
          className="bg-amber-600 text-white hover:bg-amber-700 shrink-0 h-9"
          data-testid="scan-gmail-btn"
        >
          {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning Gmail…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Scan Gmail (Last 30 Days)</>}
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="signals-filter-bar">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold mr-2">Filter:</span>
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`text-xs px-3 py-1 rounded-full transition-all ${statusFilter === f.key ? f.color + ' font-semibold shadow-sm' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : signals.length === 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="py-12 text-center">
            <Radar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-600 font-medium">No signals {statusFilter !== 'all' ? `in "${STATUS_FILTERS.find(f => f.key === statusFilter)?.label}"` : ''}</p>
            <p className="text-xs text-slate-400 mt-1">
              {statusFilter === 'pending_review' ? 'Click "Scan Gmail" to detect new signals from recent replies.' : 'Try another filter or run a fresh scan.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {signals.map(sig => (
            <Card key={sig.id} className="border border-amber-200 bg-amber-50/30" data-testid={`signal-card-${sig.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge className="bg-amber-200 text-amber-900 text-xs font-semibold">{sig.area}</Badge>
                      {sig.confidence && (
                        <Badge className={`text-[10px] ${CONFIDENCE_COLORS[sig.confidence] || 'bg-slate-100 text-slate-500'}`}>
                          {sig.confidence} Confidence
                        </Badge>
                      )}
                      <Badge className="bg-slate-100 text-slate-600 text-[10px]">
                        {sig.signal_status === 'active_scouting' ? 'Active Scouting' : sig.signal_status === 'confirmed_interest' ? 'Confirmed' : 'Under Review'}
                      </Badge>
                      <span className="text-xs text-slate-400">•</span>
                      {sig.company_profile_id ? (
                        <a
                          href={`/company-profiles/${sig.company_profile_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-semibold text-sky-700 hover:underline flex items-center gap-1"
                          data-testid={`signal-company-link-${sig.id}`}
                        >
                          {sig.company_name}
                          <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      ) : (
                        <span className="text-xs font-semibold text-slate-700">{sig.company_name}</span>
                      )}
                    </div>
                    {sig.quote && (
                      <div className="text-sm text-slate-700 italic bg-white border border-amber-200 rounded-md p-2.5 mb-2 leading-relaxed">
                        “{sig.quote}”
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                      <span>via <span className="font-medium text-slate-700">{sig.sender_name || sig.sender_email}</span></span>
                      {sig.sender_email && <span className="text-slate-400">{sig.sender_email}</span>}
                      {sig.source_subject && (
                        <>
                          <span>•</span>
                          <span className="truncate max-w-[280px]" title={sig.source_subject}>Re: {sig.source_subject}</span>
                        </>
                      )}
                      {sig.created_at && (
                        <>
                          <span>•</span>
                          <span>{new Date(sig.created_at).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {sig.status === 'pending_review' && (
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => handleAccept(sig.id, sig.area)}
                        disabled={!!actionBusy[sig.id] || !sig.company_profile_id}
                        className="bg-emerald-600 text-white hover:bg-emerald-700 h-8 text-xs"
                        title={!sig.company_profile_id ? 'No linked pharma company profile — cannot accept' : 'Log as emerging interest'}
                        data-testid={`accept-signal-${sig.id}`}
                      >
                        {actionBusy[sig.id] === 'accepting' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> Accept</>}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDismiss(sig.id)}
                        disabled={!!actionBusy[sig.id]}
                        className="h-8 text-xs text-slate-500 hover:text-rose-600"
                        data-testid={`dismiss-signal-${sig.id}`}
                      >
                        {actionBusy[sig.id] === 'dismissing' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><X className="w-3 h-3 mr-1" /> Dismiss</>}
                      </Button>
                    </div>
                  )}
                  {sig.status === 'accepted' && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs shrink-0">Accepted</Badge>
                  )}
                  {sig.status === 'dismissed' && (
                    <Badge className="bg-slate-100 text-slate-500 text-xs shrink-0">Dismissed</Badge>
                  )}
                </div>
                {!sig.company_profile_id && sig.status === 'pending_review' && (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-rose-600">
                    <AlertCircle className="w-3 h-3" />
                    No linked pharma profile — create one to accept this signal.
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
